import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Card } from '../../components/ui/Card';
import { copy } from '../../config/copy';
import { SkeletonRow } from '../../components/ui/Loader';
import { since } from '../dashboard/api';
import { PostComposer } from './PostComposer';
import { PostEngagement } from './PostEngagement';
import { posts, type Post } from './api';

/**
 * An artist's updates on their profile.
 *
 * ## The list is what the server sent, and nothing is filtered here
 *
 * A followers-only update reaches this component only when the reader is
 * allowed to have it - `PostRepository::forProfile` decides in SQL. Filtering
 * again here would imply the list might contain things it should not, which is
 * exactly the belief that leads somebody to trust a client-side check later.
 *
 * ## The audience is labelled on the post, for the author
 *
 * A "Followers" tag on your own update is the only way to tell, a week later,
 * which way you posted it. Nobody else sees the tag, because to them every
 * visible post is simply visible.
 */
export function PostList({ username, isOwner }: { username: string; isOwner: boolean }) {
  const [editing, setEditing] = useState<Post | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['posts', username],
    queryFn: () => posts.forProfile(username),
    retry: false,
  });

  /*
   * The server decides whether this section exists at all.
   *
   * It used to be gated on the `bulletins` flag read from the session - which
   * meant a signed-out reader, who has no session and therefore no flags, was
   * shown nothing even though the endpoint would have answered them. The flag
   * lives on the route; a 404 is the same answer to "switched off" and "not
   * available to you", and both mean draw nothing.
   */
  if (isError) {
    return null;
  }

  const items = data?.items ?? [];

  /*
   * Nothing at all for a visitor looking at a profile with no updates. The
   * owner still gets the section, because for them an empty one is an
   * invitation rather than a gap.
   */
  if (!isPending && items.length === 0 && !isOwner) {
    return null;
  }

  return (
    <section aria-labelledby="profile-posts" data-post-list>
      <h2
        id="profile-posts"
        className="border-b border-line pb-3 text-lg font-bold tracking-tight"
      >
        {copy['noun.post.plural']}
      </h2>

      {isPending ? (
        <div className="mt-4 flex flex-col gap-2" aria-busy="true">
          {[0, 1].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
      {isOwner && !editing && <PostComposer username={username} />}

      {items.length === 0 ? (
        <p className="text-sm text-soft">
          {isOwner
            ? 'Nothing posted yet. This is where you tell people what you are working on.'
            : 'No updates yet.'}
        </p>
      ) : (
        items.map((post) =>
          editing?.id === post.id ? (
            <PostComposer
              key={post.id}
              username={username}
              editing={post}
              onDone={() => setEditing(null)}
            />
          ) : (
            <Entry key={post.id} post={post} username={username} onEdit={() => setEditing(post)} />
          ),
        )
      )}
        </div>
      )}
    </section>
  );
}

function Entry({
  post,
  username,
  onEdit,
}: {
  post: Post;
  username: string;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => posts.remove(post.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['posts', username] }),
  });

  return (
    <Card className="flex flex-col gap-2 p-4" data-post={post.id}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] text-faint">{since(post.created_at)}</span>

        {post.edited_at && <span className="font-mono text-[11px] text-ghost">edited</span>}

        {/* Only the author sees this, and only they need it. */}
        {post.mine && post.visibility === 'followers' && (
          <span className="meta-caps text-fresh" data-post-audience>
            followers
          </span>
        )}

        {post.mine && (
          <span className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={onEdit}
              data-post-edit
              className="text-xs text-soft transition-colors hover:text-ink"
            >
              Edit
            </button>

            {/*
              Two presses to delete, and no dialog.

              A modal for one line of text is heavier than the thing it is
              protecting; a button that changes its own label is enough to stop
              the accidental press, which is the only failure worth stopping.
            */}
            <button
              type="button"
              onClick={() => (confirming ? remove.mutate() : setConfirming(true))}
              onBlur={() => setConfirming(false)}
              disabled={remove.isPending}
              data-post-delete
              className={`text-xs transition-colors ${
                confirming ? 'text-hot-text' : 'text-soft hover:text-ink'
              }`}
            >
              {confirming ? 'Sure?' : 'Delete'}
            </button>
          </span>
        )}
      </div>

      {/* `whitespace-pre-wrap`, because somebody who pressed return meant it. */}
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-ink">
        {post.body}
      </p>

      {/*
        Pictures, when the worker has finished with them.

        One goes full width; more than one goes two across. A single photograph
        shrunk into a grid cell for consistency's sake is a photograph nobody
        can see.

        `alt` falls back to empty rather than to the body text: an image with no
        description is decorative as far as a screen reader is concerned, and
        reading the post twice is worse than not describing the picture.
      */}
      {post.images.length > 0 && (
        <ul
          className={`mt-1 grid gap-2 ${post.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
          data-post-images
        >
          {post.images.map((image) => (
            <li key={image.url}>
              <img
                src={image.url}
                alt={image.alt ?? ''}
                loading="lazy"
                className="w-full rounded-[var(--radius-control)] border border-line object-cover"
              />
            </li>
          ))}
        </ul>
      )}

      <PostEngagement post={post} username={username} />
    </Card>
  );
}
